import React, { useMemo } from 'react'

import {
  cellWidthsFor,
  DESKTOP,
  DynamicSectionConfig,
  FieldType,
  getWidthStyle,
  GRID_GAP,
  MOBILE,
  resolveMargins,
  ScreenType,
  useEditor
} from '@toasttab/sites-components'

import {
  BackgroundImage,
  Block as BlockType,
  PaddingEnum
} from 'src/apollo/sites'
import {
  getBackgroundColorOrImageModule,
  getSectionPaddingModule
} from 'src/shared/components/common/editor_context/editableUtils'
import { ScreenWidth, useIsMobile } from 'src/shared/js/utils/WindowContext'

import {
  DynamicSectionConfigProvider,
  DynamicSectionLayoutProvider,
  SectionLayout,
  useDynamicSectionConfig,
  useDynamicSectionLayout
} from 'public/components/default_template/dynamic_section/DynamicSectionContext'
import {
  computeMobileOffsets,
  computeRequiredNumRows,
  sortBlocksForMobile,
  spacingEnumToVw
} from 'public/components/default_template/dynamic_section/DynamicSectionUtils'

import { resolveVerticalSpacing } from 'public/components/default_template/main_page/SectionWrapper'

import { Block, BlockWrapper } from './Block'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'

type Props = {
  blocks: BlockType[]
  numRows: number
  mobileNumRows?: number | null
  numCols?: number
  mobileNumCols?: number
  editPath: string
  padding: PaddingEnum | null | undefined
  backgroundColor?: string | null
  backgroundImage?: BackgroundImage | null
  isPopup?: boolean
  cellWidthCSS?: string // css to define the width of a cell within DynamicSection's grid
  cellHeightCSS?: string // css to define the height of a cell within DynamicSection's grid
  horizontalMargin?: number | null
}

type ContentProps = Omit<Props, 'blocks' | 'numCols' | 'mobileNumCols'>

export const DynamicSection = ({
  blocks,
  numRows,
  mobileNumRows,
  numCols,
  mobileNumCols,
  editPath,
  horizontalMargin,
  padding,
  ...contentProps
}: Props) => {
  const { isEditor } = useEditor()
  const { restaurant } = useRestaurant()
  const isMobile = useIsMobile(ScreenWidth.SMALL)
  const screenType: ScreenType = isMobile ? 'mobile' : 'desktop'

  const config: DynamicSectionConfig = useMemo(() => {
    const horizontalMargins = resolveMargins(
      horizontalMargin,
      spacingEnumToVw(restaurant?.config?.designConfig?.horizontalSpacing),
      isEditor
    )
    const maxContentWidth =
      restaurant?.config?.designConfig?.maxContentWidth ?? null

    return {
      editPath,
      isEditor,
      screenType,
      horizontalMargins,
      verticalSpacing: resolveVerticalSpacing(
        padding,
        restaurant?.config?.designConfig?.verticalSpacing
      ),
      cellWidths: cellWidthsFor(horizontalMargins, maxContentWidth),
      numCols: numCols ?? DESKTOP.NUM_COLS,
      mobileNumCols: mobileNumCols ?? MOBILE.NUM_COLS,
      maxContentWidth
    }
  }, [
    editPath,
    isEditor,
    screenType,
    horizontalMargin,
    padding,
    restaurant?.config?.designConfig?.horizontalSpacing,
    restaurant?.config?.designConfig?.verticalSpacing,
    restaurant?.config?.designConfig?.maxContentWidth,
    numCols,
    mobileNumCols
  ])

  const layout: SectionLayout = useMemo(() => {
    const mobileOrderedBlocks = sortBlocksForMobile(blocks)
    const { offsetByGuid, requiredMobileNumRows } = computeMobileOffsets(
      mobileOrderedBlocks,
      config.horizontalMargins,
      config.maxContentWidth
    )

    const requiredNumRows = computeRequiredNumRows(blocks)

    return {
      numRows: Math.max(numRows ?? 0, requiredNumRows),
      mobileNumRows: Math.max(mobileNumRows ?? 0, requiredMobileNumRows),
      requiredNumRows,
      requiredMobileNumRows,
      offsetByGuid,
      sortedBlocks: isMobile ? mobileOrderedBlocks : blocks
    }
  }, [
    blocks,
    isMobile,
    numRows,
    mobileNumRows,
    config.horizontalMargins,
    config.maxContentWidth
  ])

  return (
    <DynamicSectionConfigProvider config={config}>
      <DynamicSectionLayoutProvider layout={layout}>
        <DynamicSectionContent
          editPath={editPath}
          numRows={numRows}
          horizontalMargin={horizontalMargin}
          padding={padding}
          {...contentProps}
        />
      </DynamicSectionLayoutProvider>
    </DynamicSectionConfigProvider>
  )
}

const DynamicSectionContent = ({
  editPath,
  numRows,
  padding,
  backgroundColor,
  backgroundImage,
  isPopup,
  cellWidthCSS,
  cellHeightCSS,
  horizontalMargin
}: ContentProps) => {
  const {
    isEditor,
    dynamicModuleWrapper: DynamicModuleWrapper,
    useEditableRef
  } = useEditor()
  const {
    screenType,
    horizontalMargins,
    numCols,
    mobileNumCols,
    maxContentWidth
  } = useDynamicSectionConfig()
  const {
    numRows: renderedNumRows,
    requiredNumRows,
    requiredMobileNumRows,
    mobileNumRows,
    sortedBlocks
  } = useDynamicSectionLayout()
  const isMobile = screenType === 'mobile'

  const gridStyle = useMemo(() => {
    const calcdRows = isMobile ? mobileNumRows : renderedNumRows
    const calcdCols = isMobile ? mobileNumCols : numCols
    return gridCSS(calcdRows, calcdCols, isMobile, cellWidthCSS, cellHeightCSS)
  }, [
    isMobile,
    mobileNumRows,
    renderedNumRows,
    numCols,
    mobileNumCols,
    cellWidthCSS,
    cellHeightCSS
  ])

  const { editableRef } = useEditableRef({
    name: 'dynamic section',
    actions: ['move-up', 'move-down', 'duplicate', 'delete'],
    displayName: 'Dynamic Section',
    path: editPath,
    schema: {
      fields: [
        getSectionPaddingModule(editPath, padding),
        getBackgroundColorOrImageModule(
          editPath,
          backgroundColor,
          backgroundImage
        ),
        {
          displayName: 'Rows (desktop view)',
          type: FieldType.Number,
          path: `${editPath}.numRows`,
          value: numRows,
          validation: {
            min: Math.max(4, requiredNumRows),
            max: Math.max(60, requiredNumRows),
            step: 1
          }
        },
        {
          displayName: 'Rows (mobile view)',
          type: FieldType.Number,
          path: `${editPath}.mobileNumRows`,
          value: requiredMobileNumRows,
          validation: {
            min: Math.max(1, requiredMobileNumRows),
            max: Math.max(130, requiredMobileNumRows),
            step: 1
          }
        },
        {
          displayName: 'Horizontal Margin',
          type: FieldType.NullableSelect,
          path: `${editPath}.horizontalMargin`,
          value: horizontalMargin,
          enums: [
            { displayName: 'None', value: 0 },
            { displayName: 'Small', value: 4 },
            { displayName: 'Medium', value: 6 },
            { displayName: 'Large', value: 8 },
            { displayName: 'Extra Large', value: 10 }
          ]
        }
      ]
    }
  })

  // Mobile view always has a 4vw margin (or 1.5vw in the editor) if margin is set.
  const cssHorizontalMargin = isMobile
    ? horizontalMargins.mobile
    : horizontalMargins.desktop
  const widthStyle = getWidthStyle(cssHorizontalMargin, maxContentWidth)

  // a dynamic section within a popup is editable within the popup content form
  if (isEditor && !isPopup && DynamicModuleWrapper) {
    return (
      <DynamicModuleWrapper
        ref={editableRef}
        blockComponent={Block}
        editPath={editPath}
      />
    )
  }

  return (
    <div
      style={{
        width: widthStyle,
        display: 'flex',
        zIndex: 1,
        position: 'relative',
        marginLeft: 'auto',
        marginRight: 'auto'
      }}
      data-testid='dynamicSection'
    >
      <div
        style={{
          flex: 1,
          display: 'grid',
          ...gridStyle,
          position: 'relative'
        }}
        data-testid='dynamic-section-style-wrapper'
      >
        {sortedBlocks.map((block, index) => (
          <BlockWrapper key={block.guid} block={block}>
            <Block
              block={block}
              editPath={`${editPath}.blocks[${index}]`}
              isPopup={isPopup}
            />
          </BlockWrapper>
        ))}
      </div>
    </div>
  )
}

// The goal of this method is to scale the vertical size of the grid as the horizontal size
// changes. This maintains an aspect ratio that is similar to the original intent of the
// admin who set up their module. The original implementation of the grid only scaled
// horizontally - which will led to shapes and images having a compeltely different
// scale on various screen sizes that don't perfectly match the one shown in the editor
// when the admin set the site up.
const gridCSS = (
  numRows: number,
  numCols: number,
  isMobile: boolean,
  cellWidth?: string,
  cellHeight?: string
): React.CSSProperties => {
  if (cellWidth && cellHeight) {
    return {
      gridTemplateRows: `repeat(${numRows}, ${cellHeight})`,
      gridTemplateColumns: `repeat(${numCols}, ${cellWidth})`,
      gap: `${GRID_GAP}px`
    } as React.CSSProperties
  }

  // On mobile, we prevent empty rows by setting the min size to auto and the max to 100% to ensure content fits fully.
  const mobileGridTemplateRows = `repeat(${numRows}, minmax(auto, 100%))`

  // On desktop, we display empty rows by setting the minimum size to 36px and the maximum to max-content for full block height.
  const desktopGridTemplateRows = `repeat(${numRows}, minmax(${
    DESKTOP.CELL_HEIGHT + 'px'
  }, max-content))`

  return {
    '--gutter': `${GRID_GAP}px`,
    '--max-site-width': '100%',
    '--cell-max-width': `calc( ( var(--max-site-width, 100vw) - (${GRID_GAP}px * ${
      numCols - 1
    }) ) / ${numCols} )`,
    gridTemplateRows: isMobile
      ? mobileGridTemplateRows
      : desktopGridTemplateRows,
    gridTemplateColumns: `repeat(${numCols}, minmax(0, var(--cell-max-width)))`,
    gap: 'var(--gutter)',
    height: 'max-content'
  } as React.CSSProperties
}
